import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Spinner } from "@/components/common";
import { Lock, Mail } from "lucide-react";
import { PasoMfa } from "@/components/PasoMfa";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [nombre, setNombre] = useState("");
  const [tipoDoc, setTipoDoc] = useState("CC");
  const [numDoc, setNumDoc] = useState("");
  const [error, setError] = useState(() => {
    const aviso = sessionStorage.getItem("roge_aviso_login") || "";
    sessionStorage.removeItem("roge_aviso_login");
    return aviso;
  });
  const [loading, setLoading] = useState(false);
  const [mfa, setMfa] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") {
        const r = await login(email.trim(), password);
        if (r?.mfa) { setMfa(r); return; }
      } else {
        await register({ name: nombre.trim(), email: email.trim(), password, tipo_documento: tipoDoc, numero_documento: numDoc.trim() });
      }
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen">
      <div className="hidden flex-1 flex-col justify-between bg-vino-900 p-12 text-white lg:flex">
        <div className="flex items-center gap-3">
          <div className="rounded-xl bg-white px-4 py-3 shadow-md">
            <img src="/human-logo-full.png" alt="Human Resources" className="h-14 w-auto" />
          </div>
        </div>
        <div>
          <img src="/roge-cat.png" alt="Gato institucional Roge" data-testid="gato-roge" className="mb-5 h-32 w-auto drop-shadow-md" />
          <h1 className="font-display text-5xl font-bold leading-tight">Roge</h1>
          <p className="mt-2 text-lg text-vino-100/80">by Human RMSA</p>
          <p className="mt-6 max-w-md text-sm leading-relaxed text-vino-100/60">
            Gestión de empleados y preparación de contrataciones de HUMAN. Directorio, expedientes
            y matriz Excel oficial en un solo lugar.
          </p>
        </div>
        <p className="text-xs text-vino-100/40">Acceso restringido · Uso administrativo interno</p>
      </div>

      <div className="flex flex-1 items-center justify-center bg-background p-6">
        <div className="w-full max-w-sm animate-fade-in-up">
          <div className="mb-8 lg:hidden">
            <img src="/human-logo-full.png" alt="Human Resources" className="h-10 w-auto" />
          </div>
          <h2 className="font-display text-2xl font-bold text-slate-900">{mfa ? "Verificación en dos pasos" : mode === "login" ? "Iniciar sesión" : "Crear cuenta de empleado"}</h2>
          <p className="mt-1 text-sm text-slate-500">{mfa ? "Solo para administradores de HUMAN. Se configura una sola vez." : mode === "login" ? "Ingrese sus credenciales para continuar." : "Regístrate para completar tu expediente como empleado."}</p>

          {mfa ? <div className="mt-8"><PasoMfa inicio={mfa} onCancel={() => { setMfa(null); setPassword(""); }} onDone={() => navigate("/")} /></div> : (
          <form onSubmit={submit} className="mt-8 space-y-4">
            {mode === "register" && (
              <>
                <div>
                  <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-slate-500">Nombre completo</label>
                  <input data-testid="reg-nombre" required value={nombre} onChange={(e) => setNombre(e.target.value)} className="w-full rounded-lg border border-slate-200 bg-white py-2.5 px-3 text-sm outline-none focus:border-vino-500 focus:ring-2 focus:ring-vino-500/20" placeholder="Nombre y apellidos" />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-slate-500">Tipo</label>
                    <select value={tipoDoc} onChange={(e) => setTipoDoc(e.target.value)} className="w-full rounded-lg border border-slate-200 bg-white py-2.5 px-2 text-sm outline-none focus:border-vino-500">{["CC", "CE", "P", "PEP", "OTRO"].map((t) => <option key={t}>{t}</option>)}</select>
                  </div>
                  <div className="col-span-2">
                    <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-slate-500">N° documento</label>
                    <input data-testid="reg-doc" required value={numDoc} onChange={(e) => setNumDoc(e.target.value)} className="w-full rounded-lg border border-slate-200 bg-white py-2.5 px-3 text-sm outline-none focus:border-vino-500 focus:ring-2 focus:ring-vino-500/20" placeholder="Documento" />
                  </div>
                </div>
              </>
            )}
            <div>
              <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-slate-500">
                Correo electrónico
              </label>
              <div className="relative">
                <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  data-testid="login-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 bg-white py-2.5 pl-10 pr-3 text-sm outline-none focus:border-vino-500 focus:ring-2 focus:ring-vino-500/20"
                  placeholder="nombre@human.com"
                />
              </div>
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-slate-500">
                Contraseña
              </label>
              <div className="relative">
                <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  data-testid="login-password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 bg-white py-2.5 pl-10 pr-3 text-sm outline-none focus:border-vino-500 focus:ring-2 focus:ring-vino-500/20"
                  placeholder="••••••••"
                />
              </div>
            </div>

            {error && (
              <div data-testid="login-error" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            )}

            <button
              data-testid="login-submit"
              type="submit"
              disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-vino-700 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-vino-800 disabled:opacity-60"
            >
              {loading && <Spinner />}
              {loading ? (mode === "login" ? "Ingresando..." : "Creando...") : (mode === "login" ? "Ingresar" : "Crear cuenta")}
            </button>
          </form>
          )}

          {!mfa && <div className="mt-5 text-center text-sm text-slate-500">
            {mode === "login" ? (
              <button type="button" data-testid="switch-register" onClick={() => { setMode("register"); setError(""); }} className="font-medium text-vino-700 hover:underline">¿Eres empleado nuevo? Crea tu cuenta</button>
            ) : (
              <button type="button" data-testid="switch-login" onClick={() => { setMode("login"); setError(""); }} className="font-medium text-vino-700 hover:underline">Ya tengo cuenta · Iniciar sesión</button>
            )}
          </div>}
        </div>
      </div>
    </div>
  );
}
